import type { DehydratedState } from '@tanstack/react-query';

/** The server verified this identity's membership in this workspace. */
export type WorkspaceServerAdmission = { userId: string; orgSlug: string };

export type WorkspaceServerSnapshot = {
  admission: WorkspaceServerAdmission;
  queryState: DehydratedState;
};

export type WorkspaceAdmission =
  | { admitted: false }
  | { admitted: true; userId: string; confirmed: boolean };

/**
 * Who may see a workspace route right now.
 *
 * Client-confirmed membership admits as before. Before the client confirms,
 * a server admission for this exact workspace (checked with the request's own
 * token) admits the same user so the server-rendered page stays mounted; the
 * client then either confirms that same identity or the admission ends:
 * logged out, a different identity, or the live workspace lookup coming back
 * empty all fall back to the pending shell and redirects.
 */
export function resolveWorkspaceAdmission(args: {
  orgSlug: string | undefined;
  identity: string | null;
  authLoading: boolean;
  authenticated: boolean;
  organization: { slug: string } | null | undefined;
  serverAdmission: WorkspaceServerAdmission | null;
}): WorkspaceAdmission {
  const { orgSlug, identity, serverAdmission } = args;
  if (!orgSlug) return { admitted: false };
  const serverAdmitsRoute = serverAdmission?.orgSlug === orgSlug;
  const confirmed =
    !args.authLoading && args.authenticated && identity !== null;
  if (confirmed) {
    if (args.organization?.slug === orgSlug)
      return { admitted: true, userId: identity, confirmed: true };
    // Server-verified membership bridges the live lookup's first round trip.
    if (
      args.organization === undefined &&
      serverAdmitsRoute &&
      serverAdmission?.userId === identity
    )
      return { admitted: true, userId: identity, confirmed: true };
    return { admitted: false };
  }
  if (!args.authLoading && !args.authenticated) return { admitted: false };
  if (serverAdmission && serverAdmitsRoute)
    return { admitted: true, userId: serverAdmission.userId, confirmed: false };
  return { admitted: false };
}
